"use client"

import React, { useState } from "react"
import { ChevronDown } from "lucide-react"

const SelectContext = React.createContext({
  value: "",
  setValue: () => {},
  isOpen: false,
  setIsOpen: () => {},
})

const Select = ({ children, onValueChange, defaultValue = "" }) => {
  const [value, setValue] = useState(defaultValue)
  const [isOpen, setIsOpen] = useState(false)

  const handleValueChange = (newValue) => {
    setValue(newValue)
    setIsOpen(false)
    onValueChange?.(newValue)
  }

  return (
    <SelectContext.Provider value={{ value, setValue: handleValueChange, isOpen, setIsOpen }}>
      <div className="relative">{children}</div>
    </SelectContext.Provider>
  )
}

const SelectTrigger = ({ className = "", children }) => {
  const { isOpen, setIsOpen } = React.useContext(SelectContext)

  const baseClassName =
    "flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 disabled:cursor-not-allowed disabled:opacity-50"
  const combinedClassName = `${baseClassName} ${className}`.trim()

  return (
    <button type="button" onClick={() => setIsOpen(!isOpen)} className={combinedClassName}>
      {children}
      <ChevronDown className="h-4 w-4 opacity-50" />
    </button>
  )
}

const SelectValue = ({ placeholder }) => {
  const { value } = React.useContext(SelectContext)

  return <span className={value ? "text-gray-900" : "text-gray-500"}>{value || placeholder}</span>
}

const SelectContent = ({ children }) => {
  const { isOpen } = React.useContext(SelectContext)

  if (!isOpen) return null

  return (
    <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
      {children}
    </div>
  )
}

const SelectItem = ({ value, children }) => {
  const { setValue } = React.useContext(SelectContext)

  return (
    <button
      type="button"
      onClick={() => setValue(value)}
      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
    >
      {children}
    </button>
  )
}

export { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
